/**
 * The route table.
 *
 * Everything except `/login` sits behind `RequireAuth`, so a new page is
 * protected by default. Opting a route OUT of authentication has to be
 * deliberate, which is the safer direction for the mistake to run.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.8
 */
import { createBrowserRouter, Navigate } from 'react-router';

import { AppShell } from '../components/layout/AppShell';
import { AdminPage } from '../features/admin/AdminPage';
import { AlertsPage } from '../features/alerts/AlertsPage';
import { AnalyticsPage } from '../features/analytics/AnalyticsPage';
import { QuarterlyReportPage } from '../features/analytics/QuarterlyReportPage';
import { AssetDetailPage } from '../features/assets/AssetDetailPage';
import { AssetMapPage } from '../features/assets/AssetMapPage';
import { AssetsPage } from '../features/assets/AssetsPage';
import { LoginPage } from '../features/auth/components/LoginPage';
import { ContactDetailPage } from '../features/contacts/ContactDetailPage';
import { ContactsPage } from '../features/contacts/ContactsPage';
import { CustomerDetailPage } from '../features/customers/CustomerDetailPage';
import { CustomersPage } from '../features/customers/CustomersPage';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { MatchmakerPage } from '../features/matchmaker/MatchmakerPage';
import { PilotDetailPage } from '../features/pilots/PilotDetailPage';
import { PilotsPage } from '../features/pilots/PilotsPage';
import { PilotTimelinePage } from '../features/pilots/PilotTimelinePage';
import { SearchPage } from '../features/search/SearchPage';
import { StartupDetailPage } from '../features/startups/StartupDetailPage';
import { StartupNewPage } from '../features/startups/StartupNewPage';
import { StartupsPage } from '../features/startups/StartupsPage';
import { ROUTES } from '../lib/routes';

import { RequireAuth } from './RequireAuth';

export const router = createBrowserRouter([
  { path: ROUTES.login, element: <LoginPage /> },

  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to={ROUTES.dashboard} replace /> },
          { path: ROUTES.dashboard, element: <DashboardPage /> },
          { path: ROUTES.startups, element: <StartupsPage /> },
          /* Declared BEFORE the `:id` route so `/startups/new` is not matched
             as a startup whose id is "new". */
          {
            path: ROUTES.startupNew,
            /* eslint-disable-next-line jsx-a11y/aria-role -- our own RBAC prop */
            element: <RequireAuth role="EDITOR" />,
            children: [{ index: true, element: <StartupNewPage /> }],
          },
          { path: ROUTES.startupDetail, element: <StartupDetailPage /> },
          { path: ROUTES.pilots, element: <PilotsPage /> },
          /* Declared BEFORE the `:id` route so `/pilots/timeline` is not
             matched as a pilot whose id is "timeline". */
          { path: ROUTES.pilotTimeline, element: <PilotTimelinePage /> },
          { path: ROUTES.pilotDetail, element: <PilotDetailPage /> },
          { path: ROUTES.customers, element: <CustomersPage /> },
          { path: ROUTES.customerDetail, element: <CustomerDetailPage /> },
          { path: ROUTES.assets, element: <AssetsPage /> },
          /* Declared BEFORE the `:id` route so `/assets/map` is not matched as
             an asset whose id is "map". */
          { path: ROUTES.assetMap, element: <AssetMapPage /> },
          { path: ROUTES.assetDetail, element: <AssetDetailPage /> },
          { path: ROUTES.contacts, element: <ContactsPage /> },
          { path: ROUTES.contactDetail, element: <ContactDetailPage /> },
          { path: ROUTES.search, element: <SearchPage /> },
          { path: ROUTES.alerts, element: <AlertsPage /> },
          { path: ROUTES.reports, element: <AnalyticsPage /> },
          { path: ROUTES.reportQuarterly, element: <QuarterlyReportPage /> },
          /* Matchmaking proposes actions a viewer cannot take, so the page is
             editor-gated to match the nav (§7.1). */
          {
            path: ROUTES.matchmaker,
            /* eslint-disable-next-line jsx-a11y/aria-role -- our own RBAC prop */
            element: <RequireAuth role="EDITOR" />,
            children: [{ index: true, element: <MatchmakerPage /> }],
          },
          /* Admin panel — taxonomies, scoring, users, demo data (F9). */
          {
            path: ROUTES.admin,
            /* eslint-disable-next-line jsx-a11y/aria-role -- our own RBAC prop */
            element: <RequireAuth role="ADMIN" />,
            children: [{ index: true, element: <AdminPage /> }],
          },
        ],
      },
    ],
  },

  /* Anything else goes to the dashboard, which then redirects to login if the
     visitor is not signed in. */
  { path: '*', element: <Navigate to={ROUTES.dashboard} replace /> },
]);
